import { action, computed, observable, makeObservable } from 'mobx'
import { getAkaTitleTypes, getCountries, getLanguages, getWriterRoles } from '@App/api/routes/lib'
import { AkaTitleType, Countries, Language, OptionModel, States, WriterRoles } from '@Entities/lib'

interface CountryStates {
    raw: States[]
    options: OptionModel[]
    formOptions: OptionModel[]
}

export default class LibStore {
    private _languages: OptionModel[] = []

    private _rawCountries: Countries[] = []

    private _akaTitleTypes: OptionModel[] = []

    private _writerRoles: OptionModel[] = []

    constructor() {
        makeObservable<
            LibStore,
            '_languages' | '_rawCountries' | '_akaTitleTypes' | '_writerRoles' | 'statesByCountryId'
        >(this, {
            _languages: observable,
            _rawCountries: observable,
            _akaTitleTypes: observable,
            _writerRoles: observable,
            countries: computed,
            phoneCountryCodes: computed,
            statesByCountryId: computed({ keepAlive: true }),
            loadLanguages: action,
            loadAkaTitleTypes: action,
            loadWriterRoles: action,
            loadCountries: action,
        })
    }

    get languages() {
        return this._languages
    }

    get akaTitleTypes() {
        return this._akaTitleTypes
    }

    get writerRoles() {
        return this._writerRoles
    }

    get countries(): OptionModel[] {
        return this._rawCountries.map((data: Countries) => ({
            value: data.id.toString(),
            label: data.name,
            id: data.id.toString(),
        }))
    }

    private get statesByCountryId(): Map<number, CountryStates> {
        const statesByCountryId = new Map<number, CountryStates>()
        this._rawCountries.forEach((country) => {
            if (!country.states) {
                return
            }
            const sorted = [...country.states].sort((x, y) => x.name.localeCompare(y.name))
            statesByCountryId.set(country.id, {
                raw: country.states,
                options: sorted.map((data) => ({
                    value: data.id.toString(),
                    label: data.name,
                    id: data.id.toString(),
                })),
                formOptions: sorted.map((data) => ({
                    value: data.name,
                    label: data.name,
                    id: data.id.toString(),
                })),
            })
        })
        return statesByCountryId
    }

    public rawStatesForCountry = (countryId: number | string): States[] | undefined =>
        this.statesByCountryId.get(+countryId)?.raw

    public statesForCountry = (countryId: number | string): OptionModel[] =>
        this.statesByCountryId.get(+countryId)?.options ?? []

    public statesForCountryForForm = (countryId: number | string): OptionModel[] =>
        this.statesByCountryId.get(+countryId)?.formOptions ?? []

    public doesStateExistInCountry = (countryId: number | string, state: string): boolean =>
        this.statesForCountryForForm(countryId).some((option) => option.value === state)

    get phoneCountryCodes(): OptionModel[] {
        return this._rawCountries
            .reduce((prev, curr) => {
                const found = prev.findIndex((entry) => entry.value === curr.phoneCode.toString())
                if (found > -1) {
                    prev[found].label += ', ' + curr.code
                } else {
                    prev.push({
                        value: curr.phoneCode.toString(),
                        label: curr.code,
                        id: curr.id.toString(),
                    })
                }
                return prev
            }, [] as OptionModel[])
            .map((option) => {
                option.label = option.label + ' +' + option.value
                return option
            })
    }

    public loadLanguages = async () => {
        if (!this._languages.length) {
            const languages = await getLanguages()
            this._languages = languages.data.map((data: Language) => ({
                value: data.languageCode,
                label: data.language,
                id: data.id.toString(),
            }))
        }
    }

    public loadAkaTitleTypes = async () => {
        if (!this._akaTitleTypes.length) {
            const akaTitleTypes = await getAkaTitleTypes()
            this._akaTitleTypes = akaTitleTypes.data.map((data: AkaTitleType) => ({
                value: data.typeCode,
                label: data.description,
                id: data.id.toString(),
            }))
        }
    }

    public loadWriterRoles = async () => {
        if (!this._writerRoles.length) {
            const writerRoles = await getWriterRoles()
            this._writerRoles = writerRoles.data.map((data: WriterRoles) => ({
                value: data.roleCode,
                label: data.description,
                id: data.id.toString(),
            }))
        }
    }

    public loadCountries = async () => {
        if (!this._rawCountries.length) {
            const rawCountries = await getCountries()
            this._rawCountries = rawCountries.data.sort((x, y) =>
                x.name === 'United States' ? -1 : y.name === 'United States' ? 1 : 0
            )
        }
    }
}
